"use client";

import { SuiDerivedWallet } from "@aptos-labs/derived-wallet-sui";
import { StandardEvents, type StandardEventsChangeProperties } from "@wallet-standard/features";
import { useEffect, useState } from "react";

import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { addressesEqual } from "@/utils/address";
import { Logger } from "@/utils/logger";

interface SuiWalletLike {
  accounts: ReadonlyArray<{ address?: string | undefined }>;
  features: Record<string, unknown>;
}

interface StandardEventsFeatureLike {
  /** Typed as possibly returning nothing: wallet-standard promises an unsubscribe, wallets vary. */
  on?: (
    event: "change",
    listener: (properties: StandardEventsChangeProperties) => void,
  ) => (() => void) | undefined;
}

/**
 * Whether the Sui account this session was derived from has left the wallet's authorized list —
 * i.e. the user switched accounts inside their wallet after connecting.
 *
 * Subscribes to the underlying wallet-standard `change` event rather than the adapter's
 * `onActiveAccountChange`, which throws `Error("Not implemented")` in derived-wallet-sui.
 */
export function useSuiAccountMismatch(): boolean {
  const { wallet, suiAddress } = useDecibelWallet();
  const [hasMismatch, setHasMismatch] = useState(false);

  if (hasMismatch && (!(wallet instanceof SuiDerivedWallet) || !suiAddress)) {
    setHasMismatch(false);
  }

  useEffect(() => {
    if (!(wallet instanceof SuiDerivedWallet) || !suiAddress) return;

    const suiWallet = wallet.suiWallet as unknown as SuiWalletLike;

    const evaluate = () => {
      // Read the array live: it is mutated in place by the wallet, so a closure over the
      // initial value would report the state at subscribe time forever.
      const accounts = suiWallet.accounts;
      // A locked or disconnecting wallet reports zero accounts. Treating that as a mismatch
      // would put a non-dismissible modal on screen during teardown.
      if (accounts.length === 0) {
        setHasMismatch(false);
        return;
      }
      setHasMismatch(!accounts.some((account) => addressesEqual(account.address, suiAddress)));
    };

    evaluate();

    // A Sui wallet that cannot report account changes still connected to the correct account;
    // only live switch detection degrades, so fail open rather than break the app.
    try {
      const eventsFeature = suiWallet.features[StandardEvents] as
        | StandardEventsFeatureLike
        | undefined;
      if (!eventsFeature?.on) {
        Logger.warn("Sui wallet does not implement standard:events", { wallet: wallet.name });
        return;
      }
      const off = eventsFeature.on("change", () => evaluate());
      // Cleanup runs outside this `try`, so a wallet that subscribes but returns no unsubscribe
      // would throw during teardown — an uncatchable error in the path built to fail open.
      return () => {
        if (typeof off === "function") off();
      };
    } catch (error) {
      Logger.warn("Failed to subscribe to Sui wallet account changes", { error });
      return;
    }
  }, [wallet, suiAddress]);

  return hasMismatch;
}
